import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import {
  DialogContent,
  DialogTitle,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Dialog,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import SportsSoccerIcon from '@material-ui/icons/SportsSoccer'
import SwapHorizIcon from '@material-ui/icons/SwapHoriz'
import ViewCarouselIcon from '@material-ui/icons/ViewCarousel'
import NoteIcon from '@material-ui/icons/Note'
import { Translate } from 'react-localize-redux'
import CircularSpinner from 'components/loader/CircularSpinner'
import { createMatchHighlight } from 'api/match-highlights/api'
import TabPanel from '../../../material/TabPanel'
import AddEditGoal from './AddEditGoal'
import AddEditCard from './AddEditCard'
import AddEditReplacement from './AddEditReplacement'
import AddEditSimpleNote from './AddEditSimpleNote'
import TimeSection from './TimeSection'
import { MATCH_HIGHLIGHT_TYPES } from 'constants/match'

const useStyles = makeStyles((theme) => ({
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(0.4),
    color: theme.palette.grey[500],
  },
  tabs: {
    marginTop: '15px',
    overflow: 'unset',
    '& .MuiTabs-indicator': {
      display: 'none',
    },
    '& .MuiTabs-fixed': {
      overflow: 'unset !important',
    },
  },
  tab: {
    minWidth: '65px',
    color: '#dcdcdc',
    border: '1px solid #dcdcdc',
    borderRadius: '7px',
    padding: '15px 10px 10px',
    margin: '10px',
    transition: 'box-shadow 400ms',
    '&.MuiTab-textColorInherit.Mui-selected': {
      color: '#ffffff',
      backgroundColor: '#255f64',
      border: '1px solid #255f64',
      boxShadow: '0px 0px 20px rgb(0 0 0 / 30%);',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '10px 10px',
      minWidth: '50px',
    },
  },
  tabPanel: {
    '& .MuiBox-root': {
      paddingTop: '15px',
    },
  },
}))
const MatchHighlightsDialog = ({
  open,
  onClose,
  existingData,
  participantsList,
  matchUuid,
  onHighlightCreated,
  handleUpdate,
}) => {
  const classes = useStyles()
  const [activeTab, setActiveTab] = useState(MATCH_HIGHLIGHT_TYPES.goal)
  const [minute, setMinute] = useState(
    existingData && existingData.minute != null ? existingData.minute : 0
  )
  const [isLoading, setIsLoading] = useState(false)
  const [errorMinute, setErrorMinute] = useState(false)

  const handleChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  const handleCreateHighlight = async (data) => {
    try {
      setIsLoading(true)
      const response = await createMatchHighlight(matchUuid, data)
      onHighlightCreated({
        ...response.data,
        minute: +response.data.minute,
        second: +response.data.second,
      })
      onClose()
    } catch (error) {
      console.error('Failed to create highlight:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const resetCommonFields = () => {
    setMinute(0)
  }

  useEffect(() => {
    if (existingData) {
      setActiveTab(existingData.type)
    }
  }, [existingData])

  return (
    <Translate>
      {({ translate }) => (
        <Dialog open={open} maxWidth="sm" fullWidth onClose={onClose}>
          {isLoading && <CircularSpinner loadingcircular={isLoading} />}
          <DialogTitle>
            <Typography variant="h4" component="h4">
              {translate('matches.highlights.dialog-box-header-create')}
            </Typography>
            {onClose ? (
              <IconButton
                aria-label="close"
                size="medium"
                className={classes.closeButton}
                onClick={onClose}
              >
                <CloseIcon />
              </IconButton>
            ) : null}
          </DialogTitle>
          <DialogContent>
            <TimeSection
              existingData={existingData}
              minute={minute}
              setMinute={setMinute}
              errorMinute={errorMinute}
              setErrorMinute={setErrorMinute}
            />

            <Tabs
              value={activeTab}
              onChange={handleChange}
              className={classes.tabs}
              centered
            >
              <Tab
                icon={<SportsSoccerIcon />}
                className={classes.tab}
                style={{
                  display:
                    existingData &&
                    existingData.type !== MATCH_HIGHLIGHT_TYPES.goal
                      ? 'none'
                      : 'block',
                }}
                value={MATCH_HIGHLIGHT_TYPES.goal}
              />
              <Tab
                icon={<ViewCarouselIcon />}
                className={classes.tab}
                style={{
                  display:
                    existingData &&
                    existingData.type !== MATCH_HIGHLIGHT_TYPES.card
                      ? 'none'
                      : 'block',
                }}
                value={MATCH_HIGHLIGHT_TYPES.card}
              />
              <Tab
                icon={<SwapHorizIcon />}
                className={classes.tab}
                style={{
                  display:
                    existingData &&
                    existingData.type !== MATCH_HIGHLIGHT_TYPES.substitution
                      ? 'none'
                      : 'block',
                }}
                value={MATCH_HIGHLIGHT_TYPES.substitution}
              />
              <Tab
                icon={<NoteIcon />}
                className={classes.tab}
                style={{
                  display:
                    existingData &&
                    existingData.type !== MATCH_HIGHLIGHT_TYPES.note
                      ? 'none'
                      : 'block',
                }}
                value={MATCH_HIGHLIGHT_TYPES.note}
              />
            </Tabs>

            <TabPanel
              value={activeTab}
              index={MATCH_HIGHLIGHT_TYPES.goal}
              padding={0}
              className={classes.tabPanel}
            >
              <AddEditGoal
                onClose={onClose}
                existingData={existingData}
                participantsList={participantsList}
                handleSave={handleCreateHighlight}
                handleUpdate={handleUpdate}
                minute={minute}
                errorMinute={errorMinute}
                setErrorMinute={setErrorMinute}
                resetCommonFields={resetCommonFields}
              />
            </TabPanel>
            <TabPanel
              value={activeTab}
              index={MATCH_HIGHLIGHT_TYPES.card}
              padding={0}
              className={classes.tabPanel}
            >
              <AddEditCard
                onClose={onClose}
                existingData={existingData}
                participantsList={participantsList}
                handleSave={handleCreateHighlight}
                handleUpdate={handleUpdate}
                minute={minute}
                errorMinute={errorMinute}
                setErrorMinute={setErrorMinute}
                resetCommonFields={resetCommonFields}
              />
            </TabPanel>
            <TabPanel
              value={activeTab}
              index={MATCH_HIGHLIGHT_TYPES.substitution}
              padding={0}
              className={classes.tabPanel}
            >
              <AddEditReplacement
                onClose={onClose}
                existingData={existingData}
                participantsList={participantsList}
                handleSave={handleCreateHighlight}
                handleUpdate={handleUpdate}
                minute={minute}
                errorMinute={errorMinute}
                setErrorMinute={setErrorMinute}
                resetCommonFields={resetCommonFields}
              />
            </TabPanel>
            <TabPanel
              value={activeTab}
              index={MATCH_HIGHLIGHT_TYPES.note}
              padding={0}
              className={classes.tabPanel}
            >
              <AddEditSimpleNote
                onClose={onClose}
                existingData={existingData}
                handleSave={handleCreateHighlight}
                handleUpdate={handleUpdate}
                minute={minute}
                resetCommonFields={resetCommonFields}
                errorMinute={errorMinute}
                setErrorMinute={setErrorMinute}
              />
            </TabPanel>
          </DialogContent>
        </Dialog>
      )}
    </Translate>
  )
}
MatchHighlightsDialog.defaultProps = {
  onClose: () => 0,
  existingData: null,
  matchUuid: null,
  handleUpdate: () => 0,
}

MatchHighlightsDialog.propTypes = {
  onClose: PropTypes.func,
  participantsList: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  existingData: PropTypes.shape(),
  open: PropTypes.bool.isRequired,
  matchUuid: PropTypes.string,
  onHighlightCreated: PropTypes.func.isRequired,
  handleUpdate: PropTypes.func,
}

export default MatchHighlightsDialog
